/* src/utils/accessControl.js
 * Access control utilities for permission-based access
 */

import { apiGet } from './api';

/**
 * Get user's permissions for a workspace
 * @param {string} userId - Current user ID
 * @param {string} workspaceId - Workspace ID to check
 * @returns {Promise<{leadAccess: boolean, aiReplyAccess: boolean, isOwner: boolean}>}
 */
export async function getUserWorkspaceAccess(userId, workspaceId) {
  try {
    // If user is workspace owner, they have full access
    if (userId === workspaceId) {
      return {
        leadAccess: true,
        aiReplyAccess: true,
        isOwner: true,
      };
    }

    // Check if user is a team member
    const response = await apiGet('/getWorkspaceMembers', { workspaceId });
    
    if (response.members) {
      const member = response.members.find(
        (m) => (m.userId === userId || m.id === userId) && m.status === 'active'
      );
      
      if (member) {
        return {
          leadAccess: member.leadAccess || false,
          aiReplyAccess: member.aiReplyAccess || false,
          isOwner: false,
        };
      }
    }

    // Not a member
    return {
      leadAccess: false,
      aiReplyAccess: false,
      isOwner: false,
    };
  } catch (error) {
    console.error('Error checking workspace access:', error);
    return {
      leadAccess: false,
      aiReplyAccess: false,
      isOwner: false,
    };
  }
}

/**
 * Get all workspaces a user has access to
 * @param {string} userId - Current user ID
 * @returns {Promise<{workspaces: string[], details: Array<{id: string, role: string, name: string|null, archivedAt: string|null}>}>}
 */
export async function getUserWorkspaces(userId) {
  try {
    console.log('[ACCESS CONTROL] Getting workspaces for user:', userId);

    const data = await apiGet('/getUserWorkspaces', { userId });
    console.log('[ACCESS CONTROL] Backend returned workspaces:', data.workspaces);

    if (data.workspaces && Array.isArray(data.workspaces)) {
      return {
        workspaces: data.workspaces,
        details: Array.isArray(data.details) ? data.details : [],
      };
    }

    console.warn('[ACCESS CONTROL] Falling back to own workspace');
    return {
      workspaces: [userId],
      details: [{ id: userId, role: 'owner', name: null, archivedAt: null }],
    };
  } catch (error) {
    console.error('[ACCESS CONTROL] Error getting user workspaces:', error);
    return {
      workspaces: [userId],
      details: [{ id: userId, role: 'owner', name: null, archivedAt: null }],
    };
  }
}

/**
 * Pick the workspace to land on after login / reload.
 * saved (if still switchable) → uid workspace → first owned non-archived → first member.
 */
export function pickPrimaryWorkspace({ uid, workspaces = [], details = [], saved }) {
  if (saved && workspaces.includes(saved)) return saved;
  if (workspaces.includes(uid)) return uid;
  const firstOwned = details.find(
    (d) => d.role === 'owner' && d.id !== uid && !d.archivedAt && workspaces.includes(d.id)
  );
  if (firstOwned) return firstOwned.id;
  const firstMember = details.find((d) => d.role === 'member' && workspaces.includes(d.id));
  if (firstMember) return firstMember.id;
  return workspaces[0] || uid;
}

/**
 * @param {string} userId
 * @returns {Promise<string>}
 */
export async function getPrimaryWorkspace(userId) {
  try {
    const { workspaces, details } = await getUserWorkspaces(userId);
    return pickPrimaryWorkspace({ uid: userId, workspaces, details });
  } catch (error) {
    console.error('Error getting primary workspace:', error);
    return userId;
  }
}

/**
 * Check if user can access leads for a workspace
 * @param {string} userId - Current user ID
 * @param {string} workspaceId - Workspace ID (optional, defaults to userId's workspace)
 * @returns {Promise<boolean>}
 */
export async function canAccessLeads(userId, workspaceId = null) {
  // If no workspaceId provided, check user's own workspace
  const targetWorkspaceId = workspaceId || userId;
  
  const access = await getUserWorkspaceAccess(userId, targetWorkspaceId);
  
  // Owners always have access
  if (access.isOwner) {
    return true;
  }
  
  // Check leadAccess permission
  return access.leadAccess === true;
}

/**
 * Check if user can access AI Reply section
 * @param {string} userId - Current user ID
 * @param {string} workspaceId - Workspace ID (optional, defaults to userId's workspace)
 * @returns {Promise<boolean>}
 */
export async function canAccessAIReply(userId, workspaceId = null) {
  // If no workspaceId provided, check user's own workspace
  const targetWorkspaceId = workspaceId || userId;
  
  const access = await getUserWorkspaceAccess(userId, targetWorkspaceId);
  
  // Owners always have access
  if (access.isOwner) {
    return true;
  }
  
  // Check aiReplyAccess permission
  return access.aiReplyAccess === true;
}

/**
 * Check if user can access a specific form
 * @param {string} userId - Current user ID
 * @param {string} formWorkspaceId - Form's workspace ID
 * @returns {Promise<boolean>}
 */
export async function canAccessForm(userId, formWorkspaceId) {
  const access = await getUserWorkspaceAccess(userId, formWorkspaceId);
  
  // Owners and active members can access forms
  return access.isOwner || (access.leadAccess !== undefined || access.aiReplyAccess !== undefined);
}
